import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import MUIDataTable, { FilterType, Responsive, SelectableRows } from "mui-datatables";
import { ThemeProvider } from "@mui/material/styles";
import theme from "../../theme";
import {
    Box,
    Button,
    Typography,
    IconButton,
    Tooltip,
    AlertColor
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import RefreshIcon from "@mui/icons-material/Refresh";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";
import { getConfigForRegion, Regions } from "../../config";
import config from "../../config";
import CustomSnackbar from "../CustomSnackbar";
import ButtonWithDialog from "../DialogButton";
import "./index.css";

const { ID_CREATE_NEW } = config.app;

interface Feature {
    id: number;
    name: string;
    description?: string;
}

const FeaturesList: React.FC = () => {
    const navigate = useNavigate();
    const { headersWithAuth } = useAuth();
    const [features, setFeatures] = useState<Feature[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarSeverity, setSnackbarSeverity] = useState<AlertColor>("success");

    // Default to Canada region for features (global entity)
    const currentConfig = getConfigForRegion(Regions.Canada);

    const fetchFeatures = async () => {
        setIsLoading(true);
        try {
            const response = await axios.post(
                currentConfig.api.LICENSE_FEATURES,
                {},
                headersWithAuth
            );
            console.log("Features response:", response.data);
            setFeatures(response.data || []);
        } catch (error: any) {
            console.error("Error fetching features:", error);
            const errorMsg = error?.response?.data?.message 
                || error?.response?.data?.error
                || error?.message 
                || "Failed to fetch features";
            setSnackbarMessage(errorMsg);
            setSnackbarSeverity("error");
            setOpenSnackbar(true);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchFeatures();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const confirmDelete = async (featureId: number) => {
        if (!featureId) return;

        try {
            setIsLoading(true);
            await axios.post(
                currentConfig.api.DELETE_LICENSE_FEATURE,
                { id: featureId },
                headersWithAuth
            );
            
            setSnackbarMessage("Feature deleted successfully!");
            setSnackbarSeverity("success");
            setOpenSnackbar(true);
            fetchFeatures();
        } catch (error: any) {
            console.error("Error deleting feature:", error);
            const errorMsg = error?.response?.data?.message 
                || error?.response?.data?.error
                || error?.message 
                || "Failed to delete feature";
            setSnackbarMessage(errorMsg);
            setSnackbarSeverity("error");
            setOpenSnackbar(true);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSnackbarClose = () => {
        setOpenSnackbar(false);
    };

    const buttonViewOptions = {
        sort: false,
        filter: false,
        viewColumns: false,
        customBodyRender: (value: any) => {
            return (
                <Tooltip title="Edit">
                    <IconButton
                        onClick={() => navigate(`/feature/${value}`)}
                        color="primary"
                        size="small"
                        data-cy={`edit-feature-${value}`}
                    >
                        <EditIcon />
                    </IconButton>
                </Tooltip>
            );
        }
    };

    const buttonDeleteOptions = {
        sort: false,
        filter: false,
        viewColumns: false,
        customBodyRender: (value: any) => {
            return (
                <ButtonWithDialog
                    onDelete={() => confirmDelete(value)}
                    typeOfOperation="Delete"
                />
            );
        }
    };

    const COLUMNS = [
        {
            name: "id",
            label: " ",
            options: buttonViewOptions
        },
        {
            name: "id",
            label: " ",
            options: buttonDeleteOptions
        },
        {
            name: "name",
            label: "Feature Name",
            options: {
                filter: true,
                sort: true
            }
        },
        {
            name: "description",
            label: "Description",
            options: {
                filter: false,
                sort: false,
                customBodyRender: (value: string) => value || "-"
            }
        }
    ];

    const OPTIONS = {
        filterType: "checkbox" as FilterType,
        responsive: "standard" as Responsive,
        selectableRows: "none" as SelectableRows,
        print: false,
        download: false,
        filter: true,
        search: true,
        pagination: false,
        searchPlaceholder: "Search features",
        customToolbar: () => {
            return (
                <>
                    <Tooltip title="Refresh">
                        <IconButton onClick={fetchFeatures} disabled={isLoading}>
                            <RefreshIcon />
                        </IconButton>
                    </Tooltip>
                </>
            );
        }
    };

    const newFeatureTitle = (
        <Button
            data-cy="create-feature-button"
            onClick={() => navigate(`/feature/${ID_CREATE_NEW}`)}
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            disabled={isLoading}
        >
            Add Feature
        </Button>
    );

    const getMuiTheme = () => {
        const baseTheme = theme;
        return {
            ...baseTheme,
            components: {
                ...baseTheme.components,
                MUIDataTableHeadCell: {
                    styleOverrides: {
                        contentWrapper: {
                            justifyContent: "left"
                        }
                    }
                },
                MuiTableCell: {
                    styleOverrides: {
                        root: {
                            padding: "16px 8px"
                        }
                    }
                }
            }
        };
    };

    return (
        <ThemeProvider theme={getMuiTheme()}>
            <Box sx={{ width: "100%" }}>
                <MUIDataTable
                    title={newFeatureTitle}
                    data={features}
                    columns={COLUMNS}
                    options={OPTIONS}
                />

                <CustomSnackbar
                    open={openSnackbar}
                    handleClose={handleSnackbarClose}
                    message={snackbarMessage}
                    severity={snackbarSeverity}
                />
            </Box>
        </ThemeProvider>
    );
};

export default FeaturesList;
